import React, { useCallback, useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import {
  Banner,
  Button,
  FieldBox,
  NativeInput,
} from '@pushwoosh/dumb-components';
import { Color } from '@pushwoosh/kit-constants';
import { Horizontal } from '@pushwoosh/kit-helpers';
import { Text } from '@pushwoosh/kit-typography';

import { H1, RouterLink } from '~/src/ui-kit';
import { useEnvironment } from '~/src/contexts/environment';
import { ContentBox } from '~/src/components/ContentBox';
import { Lang, useLang } from '~/src/polyglot';

import { FormStyled, InputFieldsBox, InfoNote } from './styles';
import type { ChangePasswordProps } from './ChangePassword.types';
import { getErrorMessage } from './ChangePassword.helpers';

export function ChangePassword(props: ChangePasswordProps): ReactElement {
  const { errorMessage, savePasswordHandler } = props;
  const { getLinkByName, productName } = useEnvironment();
  const lang = useLang();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
    trigger,
    watch,
    getValues,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });
  const isAllowRegister = productName === 'Pushwoosh';

  useEffect(() => {
    const subscription = watch(() => isSubmitted && trigger());

    return () => subscription.unsubscribe();
  }, [watch, isSubmitted, trigger]);

  const validatePasswords = useCallback((): boolean => {
    const { password, confirmPassword } = getValues();

    return !!password && password === confirmPassword;
  }, [getValues]);

  return (
    <>
      <ContentBox>
        <FormStyled onSubmit={handleSubmit(savePasswordHandler)}>
          <H1><Lang message="CHANGE_PASSWORD.TITLE" /></H1>
          <Banner type="info" view="ghost" title={lang({ message: 'CHANGE_PASSWORD.BANNER_TITLE' })}>
            <Text $variant="footnote">
              <Lang message="CHANGE_PASSWORD.PASSWORD_HINT" />
            </Text>
          </Banner>
          <InputFieldsBox>
            <FieldBox
              title={lang({ message: 'CHANGE_PASSWORD.FIELD.NEW_PASSWORD' })}
              error={!!errors.password && (
                <Text $variant="footnote" $color={Color.DANGER}>
                  {getErrorMessage(errors.password, lang)}
                </Text>
              )}
            >
              <NativeInput
                autoFocus
                type="password"
                autoComplete="off"
                $isErrored={!!errors.password}
                {...register('password', { required: true, minLength: 8, validate: validatePasswords })}
              />
            </FieldBox>
            <FieldBox
              title={lang({ message: 'CHANGE_PASSWORD.FIELD.CONFIRM_PASSWORD' })}
              error={!!errors.confirmPassword && (
                <Text $variant="footnote" $color={Color.DANGER}>
                  {getErrorMessage(errors.confirmPassword, lang)}
                </Text>
              )}
            >
              <NativeInput
                type="password"
                autoComplete="off"
                $isErrored={!!errors.confirmPassword}
                {...register('confirmPassword', { required: true, minLength: 8, validate: validatePasswords })}
              />
            </FieldBox>
            {!!errorMessage && !isSubmitting && <Banner type="error" title={errorMessage} />}
          </InputFieldsBox>
          <Horizontal $alignItems="center">
            <Button
              type="submit"
              color="primary"
              isLoading={isSubmitting}
            >
              <Lang message="CHANGE_PASSWORD.BUTTON.SAVE" />
            </Button>
          </Horizontal>
          <InfoNote>
            <Lang message="CHANGE_PASSWORD.INFO_NOTE" />
          </InfoNote>
        </FormStyled>
      </ContentBox>
      <Horizontal $alignItems="center" $gap={4}>
        <RouterLink title={lang({ message: 'CHANGE_PASSWORD.LINK.SIGN_IN' })} to={getLinkByName('login')}>
          <Lang message="CHANGE_PASSWORD.LINK.SIGN_IN" />
        </RouterLink>
        {' '}
        <Lang message="CHANGE_PASSWORD.FOOTER.TO_ANOTHER_ACCOUNT" />
        {isAllowRegister && (
          <>
            {' '}
            <Lang message="CHANGE_PASSWORD.FOOTER.OR" />
            {' '}
            <RouterLink title={lang({ message: 'CHANGE_PASSWORD.LINK.SIGN_UP' })} to={getLinkByName('register')}>
              <Lang message="CHANGE_PASSWORD.LINK.SIGN_UP" />
            </RouterLink>
          </>
        )}
      </Horizontal>
    </>
  );
}
